import * as R from 'ramda'
import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectors as coreSelectors } from 'mage-core'
import { selectors as configSelectors } from 'mage-config'
import { getSearch } from 'mage-deps/connected-react-router'
import { selectors as i18nSelectors } from 'mage-i18n'
import { thunks as stateThunks } from 'mage-state'
import * as selectors from '../selectors'
import * as actions from '../actions'
import handleEducationError from '../handleEducationError'
import { differenceInMinutes } from 'date-fns'
import queryString from 'query-string'
import merge from 'deepmerge'

const propNotEq = R.complement(R.propEq)

const overrideFeatures = (features, search) => {
  const parsedQueryString = queryString.parse(search)
  const featureOverrides = Object.keys(parsedQueryString).reduce(
    (currentOverrides, queryStringParam) => {
      const [, isFeatureFlag, featureFlagName] = /^(feature)-(.*)$/.exec(queryStringParam) || []
      return isFeatureFlag
        ? {
            ...currentOverrides,
            [featureFlagName]: {
              is_enabled: parsedQueryString[queryStringParam] === 'true' ? 'true' : 'false'
            }
          }
        : currentOverrides
    },
    {}
  )
  const mergedFeatures = merge(features, featureOverrides)
  return R.filter(propNotEq('is_enabled', 'false'), mergedFeatures)
}

export default function useFetchEducationFeatures() {
  const [updatedAt, setUpdatedAt] = useState()
  const [updatedContext, setUpdatedContext] = useState()

  const dispatch = useDispatch()

  const features = useSelector(selectors.educationFeatures)
  const setFeatures = R.pipe(actions.setEducationFeatures, dispatch)

  const sessionId = useSelector(coreSelectors.sessionId)
  const country = useSelector(i18nSelectors.country)
  const app = useSelector(coreSelectors.clientId)
  const language = useSelector(i18nSelectors.language)
  const isAuthenticated = useSelector(coreSelectors.isAuthenticated)
  const accessTokenIssuedAt = useSelector(coreSelectors.getAccessTokenIssuedAt)
  const canOverrideEducationFeatures = useSelector(configSelectors.canOverrideEducationFeatures)
  const search = useSelector(getSearch)

  const setPrivateFeaturesLoaded = () =>
    isAuthenticated && dispatch(actions.setPrivateEducationFeaturesLoaded(true))

  const params = { country, language, app, sessionId }

  const context = { isAuthenticated, ...params }

  /* This effect is triggered roughtly every 5 minutes when
   * the educationClient is rebuilt due to a token refresh */
  useEffect(() => {
    const url = isAuthenticated ? '/v1/features' : '/v1/public/features'

    const shouldFetch =
      !R.equals(context, updatedContext) ||
      !updatedAt ||
      differenceInMinutes(Date.now(), updatedAt) > 60

    if (shouldFetch) {
      const onFetchDone = features => {
        const finalFeatures = canOverrideEducationFeatures
          ? overrideFeatures(features, search)
          : features
        setFeatures(finalFeatures)
        setPrivateFeaturesLoaded()
        setUpdatedAt(Date.now())
        setUpdatedContext(context)
      }

      dispatch(stateThunks.fetch(`/api/education${url}`, { method: 'GET', params }))
        .then(response => response.data.features)
        .then(onFetchDone)
        .catch(error => {
          if (!features) {
            onFetchDone({})
          }
          handleEducationError(error)
        })
    }
  }, [JSON.stringify(context), accessTokenIssuedAt])
}
